<template>
  <div
    id="AiMonitoringRegionModal"
    ref="modalElement"
    class="modal fade"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h1 class="modal-title fs-5">Perímetro de monitoramento IA</h1>
            <small v-if="camera" class="text-body-secondary">{{ camera.name }}</small>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
        </div>

        <div class="modal-body">
          <p class="mb-3">
            Toque ou clique sobre a imagem para marcar os pontos. O alerta será disparado quando qualquer parte da pessoa tocar a área destacada.
          </p>

          <div v-if="message" class="alert mb-3" :class="messageType === 'error' ? 'alert-danger' : 'alert-success'">
            {{ message }}
          </div>

          <div ref="stage" class="region-stage">
            <video
              ref="previewVideo"
              v-show="mediaMode === 'video'"
              class="region-media"
              autoplay
              muted
              playsinline
              @loadedmetadata="handleVideoMetadata"
              @error="handleVideoError"
            ></video>
            <img
              v-if="imageSource"
              v-show="mediaMode === 'image'"
              ref="previewImage"
              class="region-media"
              :src="imageSource"
              :alt="`Preview da câmera ${camera ? camera.name : ''}`"
              @load="handleImageLoad"
              @error="handleImageError"
            />

            <div v-if="!mediaReady" class="region-placeholder">
              <div v-if="loadingMedia" class="spinner-border text-light" role="status"></div>
              <i v-else class="flaticon-video-camera-1"></i>
              <span>{{ loadingMedia ? 'Carregando imagem da câmera...' : 'Preview indisponível para desenhar' }}</span>
            </div>

            <svg
              v-if="mediaReady"
              ref="overlay"
              class="region-overlay"
              :style="overlayStyle"
              viewBox="0 0 1 1"
              preserveAspectRatio="none"
              @pointerdown="addPoint"
            >
              <polygon
                v-if="closed && points.length >= 3"
                :points="svgPoints"
                class="region-polygon"
              />
              <polyline
                v-else-if="points.length"
                :points="svgPoints"
                class="region-polyline"
              />
              <circle
                v-for="(point, index) in points"
                :key="index"
                :cx="point.x"
                :cy="point.y"
                r="0.012"
                class="region-handle"
                vector-effect="non-scaling-stroke"
                @pointerdown.stop.prevent="startDragging(index, $event)"
              />
            </svg>
          </div>

          <div class="region-status mt-3">
            <span v-if="fullFrameSelected" class="badge bg-secondary">Quadro inteiro selecionado</span>
            <span v-else-if="closed" class="badge bg-success">Perímetro fechado com {{ points.length }} pontos</span>
            <span v-else class="badge bg-warning text-dark">{{ points.length }} ponto(s) — mínimo de 3</span>
          </div>

          <div class="d-flex flex-wrap gap-2 mt-3">
            <button type="button" class="btn btn-outline-secondary" :disabled="!points.length" @click="undoPoint">
              Desfazer ponto
            </button>
            <button type="button" class="btn btn-outline-secondary" :disabled="!points.length" @click="redraw">
              Redesenhar
            </button>
            <button type="button" class="btn btn-outline-primary" :disabled="closed || points.length < 3" @click="closeRegion">
              Fechar área
            </button>
            <button type="button" class="btn btn-outline-dark" @click="selectFullFrame">
              Usar quadro inteiro
            </button>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-danger" data-bs-dismiss="modal">Cancelar</button>
          <button type="button" class="btn btn-success" :disabled="!canSave || saving" @click="saveRegion">
            {{ saving ? 'Salvando...' : 'Salvar perímetro' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Hls from "hls.js";
import api from "../../config/axios.js";

type RegionPoint = { x: number; y: number };

export default defineComponent({
  name: "AiMonitoringRegionModal",
  props: {
    camera: {
      type: Object,
      default: null,
    },
  },
  emits: ["saved"],
  data() {
    return {
      points: [] as RegionPoint[],
      closed: false,
      fullFrameSelected: false,
      saving: false,
      message: "",
      messageType: "success",
      hls: null as Hls | null,
      mediaMode: "none" as "none" | "video" | "image",
      mediaReady: false,
      loadingMedia: false,
      imageSource: "",
      sourceWidth: 16,
      sourceHeight: 9,
      displayRect: { left: 0, top: 0, width: 0, height: 0 },
      resizeObserver: null as ResizeObserver | null,
      draggingIndex: null as number | null,
      ignoreCanvasPointer: false,
    };
  },
  computed: {
    svgPoints(): string {
      return this.points.map((point) => `${point.x},${point.y}`).join(" ");
    },
    overlayStyle(): Record<string, string> {
      return {
        left: `${this.displayRect.left}px`,
        top: `${this.displayRect.top}px`,
        width: `${this.displayRect.width}px`,
        height: `${this.displayRect.height}px`,
      };
    },
    canSave(): boolean {
      if (!this.camera) return false;
      if (this.fullFrameSelected) return true;
      return this.mediaReady && this.closed && this.isValidRegion(this.points);
    },
  },
  watch: {
    camera() {
      const modal = this.$refs.modalElement as HTMLElement | undefined;
      if (modal && modal.classList.contains("show")) {
        this.initializeEditor();
      }
    },
  },
  mounted() {
    const modal = this.$refs.modalElement as HTMLElement;
    modal.addEventListener("shown.bs.modal", this.initializeEditor);
    modal.addEventListener("hidden.bs.modal", this.cleanup);
    this.resizeObserver = new ResizeObserver(this.updateDisplayRect);
    this.resizeObserver.observe(this.$refs.stage as HTMLElement);
    window.addEventListener("pointermove", this.dragPoint);
    window.addEventListener("pointerup", this.stopDragging);
  },
  beforeUnmount() {
    const modal = this.$refs.modalElement as HTMLElement;
    modal.removeEventListener("shown.bs.modal", this.initializeEditor);
    modal.removeEventListener("hidden.bs.modal", this.cleanup);
    this.resizeObserver?.disconnect();
    window.removeEventListener("pointermove", this.dragPoint);
    window.removeEventListener("pointerup", this.stopDragging);
    this.cleanup();
  },
  methods: {
    initializeEditor() {
      this.cleanupMedia();
      this.message = "";
      this.saving = false;
      const currentRegion = Array.isArray(this.camera?.ai_monitoring_region)
        ? this.camera.ai_monitoring_region
        : null;
      this.points = currentRegion
        ? currentRegion.map((point) => ({ x: Number(point.x), y: Number(point.y) }))
        : [];
      this.closed = this.points.length >= 3;
      this.fullFrameSelected = !currentRegion;
      this.startPreview();
    },
    startPreview() {
      this.mediaReady = false;
      this.loadingMedia = true;
      this.imageSource = "";
      const video = this.$refs.previewVideo as HTMLVideoElement;
      const previewUrl = this.camera?.preview_url;
      if (!previewUrl) {
        this.fallbackToThumbnail();
        return;
      }

      this.mediaMode = "video";
      if (Hls.isSupported()) {
        this.hls = new Hls({
          liveSyncDurationCount: 3,
          maxLiveSyncPlaybackRate: 1.5,
          backBufferLength: 10,
        });
        this.hls.loadSource(previewUrl);
        this.hls.attachMedia(video);
        this.hls.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => undefined));
        this.hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal) this.fallbackToThumbnail();
        });
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = previewUrl;
        video.play().catch(() => undefined);
      } else {
        this.fallbackToThumbnail();
      }
    },
    fallbackToThumbnail() {
      this.mediaMode = "none";
      this.cleanupMedia();
      if (this.camera?.thumbnail) {
        this.mediaMode = "image";
        this.loadingMedia = true;
        this.imageSource = this.camera.thumbnail;
      } else {
        this.mediaMode = "none";
        this.loadingMedia = false;
      }
    },
    handleVideoMetadata() {
      const video = this.$refs.previewVideo as HTMLVideoElement;
      this.sourceWidth = video.videoWidth || 16;
      this.sourceHeight = video.videoHeight || 9;
      this.mediaReady = true;
      this.loadingMedia = false;
      this.$nextTick(this.updateDisplayRect);
    },
    handleVideoError() {
      if (this.mediaMode === "video") {
        this.fallbackToThumbnail();
      }
    },
    handleImageLoad() {
      const image = this.$refs.previewImage as HTMLImageElement;
      this.sourceWidth = image.naturalWidth || 16;
      this.sourceHeight = image.naturalHeight || 9;
      this.mediaReady = true;
      this.loadingMedia = false;
      this.$nextTick(this.updateDisplayRect);
    },
    handleImageError() {
      this.mediaReady = false;
      this.loadingMedia = false;
      this.mediaMode = "none";
    },
    updateDisplayRect() {
      const stage = this.$refs.stage as HTMLElement | undefined;
      if (!stage || !this.sourceWidth || !this.sourceHeight) return;
      const stageWidth = stage.clientWidth;
      const stageHeight = stage.clientHeight;
      const scale = Math.min(stageWidth / this.sourceWidth, stageHeight / this.sourceHeight);
      const width = this.sourceWidth * scale;
      const height = this.sourceHeight * scale;
      this.displayRect = {
        left: (stageWidth - width) / 2,
        top: (stageHeight - height) / 2,
        width,
        height,
      };
    },
    addPoint(event: PointerEvent) {
      if (this.closed || this.ignoreCanvasPointer || !this.mediaReady || this.points.length >= 50) return;
      const point = this.pointFromEvent(event);
      if (!point) return;
      this.fullFrameSelected = false;
      this.points.push(point);
    },
    startDragging(index: number, event: PointerEvent) {
      this.ignoreCanvasPointer = true;
      this.draggingIndex = index;
      (event.target as Element).setPointerCapture?.(event.pointerId);
    },
    dragPoint(event: PointerEvent) {
      if (this.draggingIndex === null) return;
      const overlay = this.$refs.overlay as SVGElement | undefined;
      if (!overlay) return;
      const point = this.pointFromEvent(event, overlay);
      if (point) this.points.splice(this.draggingIndex, 1, point);
    },
    stopDragging() {
      this.draggingIndex = null;
      window.setTimeout(() => {
        this.ignoreCanvasPointer = false;
      }, 0);
    },
    pointFromEvent(event: PointerEvent, target?: Element): RegionPoint | null {
      const overlay = target || (this.$refs.overlay as SVGElement | undefined);
      if (!overlay) return null;
      const rect = overlay.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;
      return {
        x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
        y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)),
      };
    },
    undoPoint() {
      this.closed = false;
      this.fullFrameSelected = false;
      this.points.pop();
    },
    redraw() {
      this.points = [];
      this.closed = false;
      this.fullFrameSelected = false;
      this.message = "";
    },
    closeRegion() {
      if (this.points.length < 3) return;
      if (!this.hasValidArea(this.points)) {
        this.messageType = "error";
        this.message = "O perímetro precisa formar uma área válida.";
        return;
      }
      if (this.hasSelfIntersection(this.points)) {
        this.messageType = "error";
        this.message = "As linhas do perímetro não podem se cruzar.";
        return;
      }
      this.closed = true;
      this.message = "";
    },
    selectFullFrame() {
      this.points = [];
      this.closed = false;
      this.fullFrameSelected = true;
      this.message = "";
    },
    async saveRegion() {
      if (!this.canSave || this.saving) return;
      this.saving = true;
      this.message = "";
      const monitoringRegion = this.fullFrameSelected
        ? null
        : this.points.map((point) => ({
            x: Number(point.x.toFixed(6)),
            y: Number(point.y.toFixed(6)),
          }));
      try {
        const response = await api.axios.put(`/api/cameras/${this.camera.id}/ai-monitoring-region`, {
          monitoring_region: monitoringRegion,
        });
        this.messageType = "success";
        this.message = response.data.message || "Perímetro salvo com sucesso.";
        this.$emit("saved", {
          cameraId: this.camera.id,
          region: response.data.ai_monitoring_region,
          sync: response.data.monitoring_sync,
        });
      } catch (error: unknown) {
        const apiError = error as { response?: { data?: { message?: string } } };
        this.messageType = "error";
        this.message = apiError.response?.data?.message || "Não foi possível salvar o perímetro.";
      } finally {
        this.saving = false;
      }
    },
    hasSelfIntersection(points: RegionPoint[]): boolean {
      const count = points.length;
      for (let first = 0; first < count; first += 1) {
        const firstNext = (first + 1) % count;
        for (let second = first + 1; second < count; second += 1) {
          const secondNext = (second + 1) % count;
          if (first === second || firstNext === second || secondNext === first) continue;
          if (this.segmentsIntersect(points[first], points[firstNext], points[second], points[secondNext])) return true;
        }
      }
      return false;
    },
    hasValidArea(points: RegionPoint[]): boolean {
      if (points.length < 3) return false;
      const area = points.reduce((total, point, index) => {
        const next = points[(index + 1) % points.length];
        return total + point.x * next.y - next.x * point.y;
      }, 0) / 2;
      return Math.abs(area) > 0.0000001;
    },
    isValidRegion(points: RegionPoint[]): boolean {
      return points.length >= 3 && this.hasValidArea(points) && !this.hasSelfIntersection(points);
    },
    segmentsIntersect(a: RegionPoint, b: RegionPoint, c: RegionPoint, d: RegionPoint): boolean {
      const orientation = (p: RegionPoint, q: RegionPoint, r: RegionPoint) => {
        const value = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
        if (Math.abs(value) < 0.0000001) return 0;
        return value > 0 ? 1 : 2;
      };
      const onSegment = (p: RegionPoint, q: RegionPoint, r: RegionPoint) =>
        q.x <= Math.max(p.x, r.x) && q.x >= Math.min(p.x, r.x)
        && q.y <= Math.max(p.y, r.y) && q.y >= Math.min(p.y, r.y);
      const o1 = orientation(a, b, c);
      const o2 = orientation(a, b, d);
      const o3 = orientation(c, d, a);
      const o4 = orientation(c, d, b);
      if (o1 !== o2 && o3 !== o4) return true;
      return (o1 === 0 && onSegment(a, c, b)) || (o2 === 0 && onSegment(a, d, b))
        || (o3 === 0 && onSegment(c, a, d)) || (o4 === 0 && onSegment(c, b, d));
    },
    cleanupMedia() {
      this.hls?.destroy();
      this.hls = null;
      const video = this.$refs.previewVideo as HTMLVideoElement | undefined;
      if (video) {
        video.pause();
        video.removeAttribute("src");
        video.load();
      }
      this.mediaReady = false;
    },
    cleanup() {
      this.mediaMode = "none";
      this.cleanupMedia();
      this.loadingMedia = false;
      this.imageSource = "";
      this.draggingIndex = null;
    },
  },
});
</script>

<style scoped>
.region-stage {
  position: relative;
  width: 100%;
  height: min(62vh, 680px);
  min-height: 320px;
  overflow: hidden;
  border-radius: 7px;
  background: #050505;
}

.region-media {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
}

.region-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #fff;
  font-size: 16px;
}

.region-placeholder i {
  font-size: 42px;
}

.region-overlay {
  position: absolute;
  cursor: crosshair;
  touch-action: none;
}

.region-polygon {
  fill: rgba(255, 42, 42, 0.28);
  stroke: #ff3131;
  stroke-width: 3;
  vector-effect: non-scaling-stroke;
}

.region-polyline {
  fill: none;
  stroke: #ffdd33;
  stroke-width: 3;
  vector-effect: non-scaling-stroke;
}

.region-handle {
  fill: #fff;
  stroke: #ff3131;
  stroke-width: 3;
  cursor: grab;
}

.region-handle:active {
  cursor: grabbing;
}

@media (max-width: 767px) {
  .region-stage {
    height: 52vh;
    min-height: 250px;
  }
}
</style>
